import React from "react";
import styled, { createGlobalStyle } from "styled-components";
import Layout from "../components/Layout";
import { GlobalStyle } from "../pages/HomePage";
import { graphql, Link } from "gatsby";
import ShareIcons from "../components/ShareIcons";
import Adsense from "react-adsense";
import ReactGA from "react-ga";
import ReactPlayer from "react-player";
import AdCardInDetailPage from "../components/AdCardInDetailPage";
import SEO from "../components/SEO";
import { optimizeImageUrl } from "../lib/prismicImage";
ReactGA.initialize("UA-141669883-1");

/* ─── Review-only theme tokens ─── */

const ReviewStyle = createGlobalStyle`
  :root {
    --star-fill:        #fbbf24;
    --star-empty:       #d1d5db;
    --rating-accent:    #eab308;
    --rating-card-from: #fefce8;
    --rating-card-to:   #fff7ed;
    --rating-card-border:#fde68a;
  }
  html.dark {
    --star-empty:       #4b5563;
    --rating-card-from: #1e293b;
    --rating-card-to:   #1e293b;
    --rating-card-border:#a16207;
  }
`;

/* ─── Layout ─── */

const PageWrap = styled.section`
  background: var(--bg);
  padding: 1.5rem 0 2rem;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) { padding: 0 1.5rem; }
  @media (min-width: 1024px) { padding: 0 2rem; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  @media (min-width: 1024px) { grid-template-columns: 2fr 1fr; }
`;

const Main = styled.div`
  min-width: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
`;

const Aside = styled.aside`
  min-width: 0;
  @media (min-width: 1024px) {
    position: sticky;
    top: 5rem;
    align-self: start;
  }
`;

const AdWrap = styled.div`
  width: 100%;
  margin-bottom: 1.5rem;
`;

/* ─── Header ─── */

const ArticleTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 900;
  color: var(--text-primary);
  line-height: 1.2;
  letter-spacing: -0.01em;
  margin: 0 0 1.5rem;
  @media (min-width: 640px) { font-size: 1.875rem; }
  @media (min-width: 1024px) { font-size: 2.25rem; }
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border);
`;

const Badge = styled.span`
  display: inline-block;
  background: var(--rating-accent);
  color: #000;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: 0.25rem;
`;

const MetaRating = styled.div`
  margin-left: auto;
`;

/* ─── Hero ─── */

const HeroWrap = styled.div`
  position: relative;
  margin-bottom: 1rem;
  border-radius: 0.5rem;
  overflow: hidden;
  background: var(--bg-tertiary);
`;

const HeroImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  max-height: 475px;
  object-fit: cover;
`;

const VideoTrigger = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 1rem;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background 0.2s;
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  right: 1rem;
  max-width: 24rem;
  &:hover { background: rgba(0, 0, 0, 0.85); }
`;

const VideoTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 500;
  color: #fff;
  margin: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const TrailerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--text-primary);
  color: var(--bg);
  border: none;
  border-radius: 0.375rem;
  padding: 0.6rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.85; }
`;

const ShareRow = styled.div`
  margin: 1.5rem 0;
  padding: 1rem 0;
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
`;

/* ─── Rating ─── */

const StarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  .star-fill  { stop-color: var(--star-fill); }
  .star-empty { stop-color: var(--star-empty); }
`;

const StarGroup = styled.div`
  display: flex;
  gap: 0.125rem;
`;

const RatingValue = styled.span`
  color: var(--text-secondary);
  font-weight: 700;
  font-size: 0.9375rem;
  white-space: nowrap;
`;

const RatingCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: linear-gradient(90deg, var(--rating-card-from), var(--rating-card-to));
  border: 1px solid var(--rating-card-border);
  border-radius: 0.5rem;
  padding: 1.25rem;
  margin-bottom: 2rem;
  @media (min-width: 640px) { padding: 1.5rem; }
`;

const RatingCardLabel = styled.h3`
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.5rem;
`;

const RatingBig = styled.div`
  text-align: right;
  flex-shrink: 0;
`;

const RatingBigNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 900;
  line-height: 1;
  color: var(--rating-accent);
  @media (min-width: 640px) { font-size: 3.75rem; }
`;

const RatingBigCaption = styled.div`
  font-size: 0.8125rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
`;

/* ─── Body ─── */

const ArticleBody = styled.article`
  margin-bottom: 2rem;
`;

const Paragraph = styled.p`
  font-size: 1.0625rem;
  color: var(--text-secondary);
  line-height: 1.8;
  letter-spacing: 0.01em;
  margin: 0 0 1.5rem;
  @media (min-width: 640px) { font-size: 1.125rem; }
`;

const SectionHeading = styled.h2`
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--text-primary);
  margin: 2rem 0 1rem;
  padding-left: 0.75rem;
  border-left: 4px solid var(--rating-accent);
  line-height: 1.3;
`;

const PointList = styled.ul`
  list-style: none;
  margin: 0 0 1.5rem;
  padding: 0;
`;

const PointItem = styled.li`
  position: relative;
  padding-left: 1.75rem;
  margin-bottom: 0.625rem;
  font-size: 1.0625rem;
  line-height: 1.6;
  color: var(--text-secondary);
  &::before {
    content: ${props => (props.negative ? "'\\2715'" : "'\\2713'")};
    position: absolute;
    left: 0;
    top: 0;
    font-weight: 700;
    color: ${props => (props.negative ? "#ef4444" : "#16a34a")};
  }
`;

const CreditsCard = styled.div`
  background: var(--bg-secondary);
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  padding: 1.25rem;
  margin-bottom: 2rem;
`;

const CreditsTitle = styled.h3`
  font-size: 0.8125rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
  margin: 0 0 0.875rem;
`;

const CreditRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  align-items: baseline;
  gap: 0.125rem;
  padding: 0.5rem 0;
  border-top: 1px solid var(--border);
  &:first-of-type { border-top: none; padding-top: 0; }
  @media (min-width: 480px) {
    grid-template-columns: 9rem 1fr;
    gap: 1rem;
  }
`;

const CreditKey = styled.span`
  font-size: 0.8125rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
`;

const CreditValue = styled.span`
  font-size: 0.9375rem;
  color: var(--text-secondary);
  line-height: 1.5;
`;

/* ─── Related ─── */

const RelatedSection = styled.section`
  margin-top: 3rem;
`;

const RelatedTitle = styled.h2`
  font-size: 1.375rem;
  font-weight: 800;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin: 0 0 1.5rem;
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  @media (min-width: 640px) { grid-template-columns: 1fr 1fr; }
  @media (min-width: 1024px) { grid-template-columns: repeat(3, 1fr); }
`;

const RelatedCardImg = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  overflow: hidden;
  background: var(--bg-tertiary);
  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
`;

const RelatedCardContent = styled.div`
  padding: 0.875rem;
`;

const RelatedCardTitle = styled.h3`
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--compact-title);
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.45;
  transition: color 0.2s;
`;

const cardStyles = `
  display: block;
  text-decoration: none;
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.2s;
  cursor: pointer;
  &:hover { box-shadow: 0 6px 20px rgba(0,0,0,0.15); transform: translateY(-2px); }
  &:hover img { transform: scale(1.05); }
  &:hover h3 { color: var(--link); }
`;

const RelatedCardExternal = styled.a`${cardStyles}`;
const RelatedCardInternal = styled(Link)`${cardStyles}`;

const CardRating = styled.div`
  padding: 0 0.875rem 0.875rem;
  .star-fill  { stop-color: var(--star-fill); }
  .star-empty { stop-color: var(--star-empty); }
`;

/* ─── Video modal ─── */

const VideoOverlay = styled.div`
  position: fixed;
  z-index: 999;
  height: 100vh;
  width: 100vw;
  top: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VideoContainer = styled.div`
  width: 100%;
  max-width: 900px;
  padding: 1rem;
`;

const GoBackBtn = styled.button`
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 9999px;
  cursor: pointer;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  transition: background 0.2s;
  &:hover { background: rgba(255, 255, 255, 0.35); }
`;

/* ─── Stars ─── */

const STAR_PATH =
    "M10 15.27L16.18 19l-1.64-7.03L20 7.24l-7.19-.61L10 0 7.19 6.63 0 7.24l5.46 4.73L3.82 19z";

/**
 * A single star filled left-to-right by `percent` (0-100) using a two-stop
 * gradient whose stops sit at the same offset — that hard edge is what makes
 * half/quarter stars (1.5, 1.75, …) render as a partial fill instead of
 * snapping to a whole star.
 */
const Star = ({ id, percent, size }) => (
    <svg
        width={size}
        height={Math.round(size * 0.95)}
        viewBox="0 0 20 19"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
    >
        <defs>
            <linearGradient id={id}>
                {/* stopColor is a fallback; the CSS classes above win and give us dark mode. */}
                <stop className="star-fill" offset={`${percent}%`} stopColor="#fbbf24" />
                <stop className="star-empty" offset={`${percent}%`} stopColor="#d1d5db" />
            </linearGradient>
        </defs>
        <path d={STAR_PATH} fill={`url(#${id})`} fillRule="evenodd" />
    </svg>
);

const parseRating = value => {
    const n = parseFloat(value);
    if (isNaN(n)) return null;
    return Math.max(0, Math.min(5, n));
};

// 3 -> "3", 3.50 -> "3.5", 1.75 -> "1.75"
const formatRating = n => String(parseFloat(n.toFixed(2)));

const StarRating = ({ value, idPrefix, size = 20, showValue = true }) => {
    const rating = parseRating(value);
    if (rating === null) return null;
    return (
        <StarRow role="img" aria-label={`Rated ${formatRating(rating)} out of 5`}>
            <StarGroup>
                {[0, 1, 2, 3, 4].map(i => (
                    <Star
                        key={i}
                        id={`${idPrefix}-star-${i + 1}`}
                        size={size}
                        percent={Math.round(Math.min(1, Math.max(0, rating - i)) * 100)}
                    />
                ))}
            </StarGroup>
            {showValue && <RatingValue>{formatRating(rating)}/5</RatingValue>}
        </StarRow>
    );
};

/* ─── Content parsing ───
 * Prismic gives the review back as a flat list of paragraphs. Reviews follow a
 * house style ("Story:", "Plus Points:", … and a trailing credits block), so we
 * lift that structure into headings, plus/minus lists and a credits table
 * instead of rendering one undifferentiated wall of text.
 */

const CREDIT_KEYS = new Set([
    "banner", "banners", "production", "production house", "producer", "producers",
    "co-producer", "co-producers", "presenter", "presented by", "director",
    "directors", "writer", "writers", "screenplay", "dialogues", "music",
    "music director", "songs", "background score", "cinematography",
    "cinematographer", "dop", "editing", "editor", "art director", "art",
    "choreography", "lyrics", "runtime", "running time", "duration",
    "certificate", "censor", "release date", "released on", "rating", "platform",
    "ott", "language", "genre", "cast", "starring",
]);

const asCredit = text => {
    const idx = text.indexOf(":");
    if (idx <= 0 || idx === text.length - 1) return null;
    const key = text.slice(0, idx).trim();
    const value = text.slice(idx + 1).trim();
    if (!value || key.length > 24) return null;
    if (!CREDIT_KEYS.has(key.toLowerCase())) return null;
    return { key, value };
};

const isHeading = text => text.length <= 40 && text.endsWith(":");

const LIST_SECTIONS = {
    plus: "plus", "plus points": "plus", positives: "plus", pros: "plus",
    highlights: "plus", upside: "plus",
    minus: "minus", "minus points": "minus", negatives: "minus", cons: "minus",
    drawbacks: "minus", downside: "minus",
};

const buildBlocks = raw => {
    const paras = (raw || [])
        .map(r => (r && r.text ? r.text.trim() : ""))
        .filter(Boolean);

    // Credits only count as credits when they sit in the trailing run, so a
    // mid-article line like "Music: Ravi Basrur" in prose is left alone.
    let cut = paras.length;
    while (cut > 0 && asCredit(paras[cut - 1])) cut -= 1;
    const credits = paras.length - cut >= 2 ? paras.slice(cut).map(asCredit) : [];
    const body = credits.length ? paras.slice(0, cut) : paras;

    const blocks = [];
    let list = null;
    body.forEach(text => {
        if (isHeading(text)) {
            const label = text.slice(0, -1).trim();
            list = LIST_SECTIONS[label.toLowerCase()] || null;
            blocks.push({ type: "heading", text: label });
            return;
        }
        // Long prose under a Plus/Minus heading is a paragraph, not a bullet.
        if (list && text.length <= 140) {
            blocks.push({ type: list, text });
            return;
        }
        blocks.push({ type: "para", text });
    });

    return { blocks, credits };
};

const renderBlocks = blocks => {
    const out = [];
    let bucket = null;

    const flush = () => {
        if (!bucket) return;
        out.push(
            <PointList key={`list-${out.length}`}>
                {bucket.items.map((text, i) => (
                    <PointItem key={i} negative={bucket.type === "minus"}>
                        {text}
                    </PointItem>
                ))}
            </PointList>
        );
        bucket = null;
    };

    blocks.forEach((block, i) => {
        if (block.type === "plus" || block.type === "minus") {
            if (!bucket || bucket.type !== block.type) {
                flush();
                bucket = { type: block.type, items: [] };
            }
            bucket.items.push(block.text);
            return;
        }
        flush();
        if (block.type === "heading") {
            out.push(<SectionHeading key={i}>{block.text}</SectionHeading>);
        } else {
            out.push(<Paragraph key={i}>{block.text}</Paragraph>);
        }
    });
    flush();
    return out;
};

/* ─── Component ─── */

class Article extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            showVideo: false,
        };
    }

    componentDidMount() {
        const ele = document.getElementById("ipl-progress-indicator");
        if (ele) ele.outerHTML = "";
    }

    render() {
        const prismicReviewarticle = this.props.data.prismicReviewarticle;
        const uidPath = prismicReviewarticle.uid;
        ReactGA.pageview(uidPath);
        ReactGA.event({ category: "Review", action: "Card Click" });

        const article = prismicReviewarticle.data;
        const hasImg = article.img && article.img.url;
        const hasEmbed = article.embed && article.embed.embed_url !== null;

        const title = article.title && article.title.text;
        const firstPara = article.content && article.content.raw && article.content.raw[0] && article.content.raw[0].text;
        const description = firstPara
            ? firstPara.slice(0, 160).trim()
            : `Review: ${title || "Latest review"} on Andhra Headlines.`;
        const datePublished = article.datetime || prismicReviewarticle.first_publication_date;
        const dateModified = prismicReviewarticle.last_publication_date;
        const tags = (prismicReviewarticle.tags || []).filter(t => t && t !== "featured");
        const relatedArticles = (this.props.data.relatedArticles && this.props.data.relatedArticles.edges) || [];
        const relatedReviews = ((this.props.data.relatedReviews && this.props.data.relatedReviews.edges) || [])
            .filter(e => e.node.uid !== uidPath)
            .slice(0, 3);

        const rating = parseRating(article.rating);
        const { blocks, credits } = buildBlocks(article.content && article.content.raw);

        return (
            <>
                <SEO
                    title={title}
                    description={description}
                    image={hasImg ? article.img.url : undefined}
                    pathname={`/${uidPath}`}
                    article
                    datePublished={datePublished}
                    dateModified={dateModified}
                    keywords={tags}
                />
                <GlobalStyle />
                <ReviewStyle />
                <Layout>
                    <PageWrap>
                        <Container>
                            <AdWrap className="ad-container">
                                <Adsense.Google
                                    client="ca-pub-5630104718741028"
                                    slot="9699731559"
                                    style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', minHeight: 90 }}
                                    format="auto"
                                    responsive="true"
                                />
                            </AdWrap>

                            <Grid>
                                <Main>
                                    {/* ── Header ── */}
                                    <ArticleTitle>{title}</ArticleTitle>
                                    <MetaRow>
                                        <Badge>Movie Review</Badge>
                                        {rating !== null && (
                                            <MetaRating>
                                                <StarRating value={rating} idPrefix="header-rating" />
                                            </MetaRating>
                                        )}
                                    </MetaRow>

                                    {/* ── Hero ── */}
                                    {hasImg && (
                                        <HeroWrap>
                                            <HeroImage
                                                src={optimizeImageUrl(article.img.url, 1000)}
                                                alt={title}
                                            />
                                            {hasEmbed && (
                                                <VideoTrigger onClick={() => this.setState({ showVideo: true })}>
                                                    <svg width="36" height="36" viewBox="0 0 24 24" fill="white" opacity="0.9">
                                                        <path d="M8 5v14l11-7z" />
                                                    </svg>
                                                    <VideoTitle>{article.embed && article.embed.title}</VideoTitle>
                                                </VideoTrigger>
                                            )}
                                        </HeroWrap>
                                    )}

                                    {!hasImg && hasEmbed && (
                                        <TrailerButton onClick={() => this.setState({ showVideo: true })}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M8 5v14l11-7z" />
                                            </svg>
                                            {(article.embed && article.embed.title) || "Watch trailer"}
                                        </TrailerButton>
                                    )}

                                    {/* ── Share ── */}
                                    <ShareRow>
                                        <ShareIcons path={uidPath} />
                                    </ShareRow>

                                    {/* ── Rating card ── */}
                                    {rating !== null && (
                                        <RatingCard>
                                            <div>
                                                <RatingCardLabel>Our Rating</RatingCardLabel>
                                                <StarRating value={rating} idPrefix="card-rating" />
                                            </div>
                                            <RatingBig>
                                                <RatingBigNumber>{formatRating(rating)}</RatingBigNumber>
                                                <RatingBigCaption>out of 5</RatingBigCaption>
                                            </RatingBig>
                                        </RatingCard>
                                    )}

                                    {/* ── Body ── */}
                                    <ArticleBody>{renderBlocks(blocks)}</ArticleBody>

                                    {credits.length > 0 && (
                                        <CreditsCard>
                                            <CreditsTitle>Movie Details</CreditsTitle>
                                            {credits.map((c, i) => (
                                                <CreditRow key={i}>
                                                    <CreditKey>{c.key}</CreditKey>
                                                    <CreditValue>{c.value}</CreditValue>
                                                </CreditRow>
                                            ))}
                                        </CreditsCard>
                                    )}

                                    <AdWrap className="ad-container">
                                        <Adsense.Google
                                            client="ca-pub-5630104718741028"
                                            slot="9699731559"
                                            style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', minHeight: 90 }}
                                            format="auto"
                                            responsive="true"
                                        />
                                    </AdWrap>

                                    {/* ── Related reviews ── */}
                                    {relatedReviews.length > 0 && (
                                        <RelatedSection>
                                            <RelatedTitle>Related Reviews</RelatedTitle>
                                            <RelatedGrid>
                                                {relatedReviews.map((e, i) => {
                                                    const r = e.node;
                                                    const rTitle = r.data && r.data.title && r.data.title.text ? r.data.title.text : '';
                                                    const rImg = r.data && r.data.img && r.data.img.url ? r.data.img.url : null;
                                                    const rRating = parseRating(r.data && r.data.rating);
                                                    return (
                                                        <RelatedCardInternal key={i} to={`/${r.uid}/`}>
                                                            <RelatedCardImg>
                                                                {rImg && <img src={optimizeImageUrl(rImg, 320)} alt={rTitle} />}
                                                            </RelatedCardImg>
                                                            <RelatedCardContent>
                                                                <RelatedCardTitle>{rTitle}</RelatedCardTitle>
                                                            </RelatedCardContent>
                                                            {rRating !== null && (
                                                                <CardRating>
                                                                    <StarRating
                                                                        value={rRating}
                                                                        idPrefix={`related-${i}-rating`}
                                                                        size={14}
                                                                        showValue={false}
                                                                    />
                                                                </CardRating>
                                                            )}
                                                        </RelatedCardInternal>
                                                    );
                                                })}
                                            </RelatedGrid>
                                        </RelatedSection>
                                    )}

                                    {/* ── Related news ── */}
                                    {relatedArticles.filter(e => e.node.uid !== uidPath).length > 0 && (
                                        <RelatedSection>
                                            <RelatedTitle>Related News</RelatedTitle>
                                            <RelatedGrid>
                                                {relatedArticles
                                                    .filter(e => e.node.uid !== uidPath)
                                                    .slice(0, 6)
                                                    .map((e, i) => {
                                                        const a = e.node;
                                                        const isExternal = !!(a.data && a.data.third_party_url);
                                                        const aTitle = a.data && a.data.title && a.data.title.text ? a.data.title.text : '';
                                                        const imgUrl = a.data && a.data.img && a.data.img.url ? a.data.img.url : null;
                                                        const CardEl = isExternal ? RelatedCardExternal : RelatedCardInternal;
                                                        const linkProps = isExternal
                                                            ? { href: a.data.third_party_url.startsWith('http') ? a.data.third_party_url : `https://${a.data.third_party_url}`, target: '_blank', rel: 'noopener noreferrer' }
                                                            : { to: `/${a.uid}/` };
                                                        return (
                                                            <CardEl key={i} {...linkProps}>
                                                                <RelatedCardImg>
                                                                    {imgUrl && <img src={optimizeImageUrl(imgUrl, 320)} alt={aTitle} />}
                                                                </RelatedCardImg>
                                                                <RelatedCardContent>
                                                                    <RelatedCardTitle>{aTitle}</RelatedCardTitle>
                                                                </RelatedCardContent>
                                                            </CardEl>
                                                        );
                                                    })
                                                }
                                            </RelatedGrid>
                                        </RelatedSection>
                                    )}
                                </Main>

                                {/* ── Sidebar ── */}
                                <Aside>
                                    <AdCardInDetailPage
                                        client="ca-pub-5630104718741028"
                                        slot="9368762673"
                                        style={{ display: 'block', height: 280, maxWidth: 336, width: '100%', marginLeft: 'auto', marginRight: 'auto' }}
                                        layout="-6t+ed+2i-1n-4w"
                                        format="fluid"
                                    />
                                </Aside>
                            </Grid>

                            <div className="ad-container" style={{ marginTop: '2rem' }}>
                                <Adsense.Google
                                    client="ca-pub-5630104718741028"
                                    slot="9699731559"
                                    style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', minHeight: 90 }}
                                    format="auto"
                                    responsive="true"
                                />
                            </div>
                        </Container>
                    </PageWrap>
                </Layout>

                {/* ── Video Modal ── */}
                {this.state.showVideo && (
                    <VideoOverlay>
                        <VideoContainer>
                            <GoBackBtn onClick={() => this.setState({ showVideo: false })}>
                                ← Go Back
                            </GoBackBtn>
                            <ReactPlayer
                                url={article.embed.embed_url}
                                playing
                                controls
                                volume={0.05}
                                height={500}
                                width="100%"
                                light
                                youtubeConfig={{ playerVars: { showinfo: 1, modestBranding: 1 } }}
                            />
                        </VideoContainer>
                    </VideoOverlay>
                )}
            </>
        );
    }
}

export default Article;

export const pageQuery = graphql`
    query ReviewArticleBySlug($uid: String!, $tags: [String]!) {
        prismicReviewarticle(uid: { eq: $uid }) {
            uid
            tags
            first_publication_date
            last_publication_date
            data {
                title { text }
                rating
                datetime
                img { url }
                embed {
                    embed_url
                    thumbnail_url
                    author_url
                    title
                }
                content { raw { text } }
            }
        }
        relatedReviews: allPrismicReviewarticle(
            filter: {
                uid: { ne: $uid }
                data: { active: { eq: "Y" } }
            }
            sort: { fields: [data___datetime], order: DESC }
            limit: 4
        ) {
            edges {
                node {
                    uid
                    data {
                        title { text }
                        img { url }
                        rating
                    }
                }
            }
        }
        relatedArticles: allPrismicArticle(
            filter: {
                tags: { in: $tags }
                uid: { ne: $uid }
                data: { active: { eq: "Y" } }
            }
            sort: { fields: [first_publication_date], order: DESC }
            limit: 12
        ) {
            edges {
                node {
                    uid
                    data {
                        third_party_url
                        title { text }
                        img { url }
                        datetime
                    }
                }
            }
        }
    }
`;
